import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { TREE_CONFIG } from '../../utils/constants'
import { generateChaosPosition, generateFoliagePosition } from '../../utils/positions'

interface FoliageProps {
  progress: number
}

// 顶点着色器
const vertexShader = `
  attribute vec3 chaosPosition;
  attribute vec3 targetPosition;
  attribute float size;
  attribute vec3 color;
  
  uniform float progress;
  uniform float time;
  
  varying vec3 vColor;
  varying float vProgress;
  
  void main() {
    vColor = color;
    vProgress = progress;
    
    // 在混沌和目标位置之间插值
    vec3 pos = mix(chaosPosition, targetPosition, progress);
    
    // 添加轻微的浮动动画
    float floatAmount = (1.0 - progress) * 0.5;
    pos.y += sin(time * 2.0 + position.x * 10.0) * floatAmount;
    pos.x += cos(time * 1.5 + position.z * 10.0) * floatAmount * 0.5;
    
    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    
    // 根据进度调整大小
    float dynamicSize = size * (0.5 + progress * 0.5);
    gl_PointSize = dynamicSize * (300.0 / -mvPosition.z);
    
    gl_Position = projectionMatrix * mvPosition;
  }
`

// 片段着色器 - 适中亮度的绿色
const fragmentShader = `
  varying vec3 vColor;
  varying float vProgress;
  
  void main() {
    // 创建圆形粒子
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);
    
    if (dist > 0.5) {
      discard;
    }
    
    // 边缘柔化
    float alpha = 1.0 - smoothstep(0.3, 0.5, dist);
    
    // 适中效果 - 中等亮度，轻微金色光晕
    float glow = 1.0 - dist * 1.8;
    vec3 goldTint = vec3(1.0, 0.85, 0.3); // 金色
    vec3 finalColor = vColor * 1.1 + goldTint * glow * 0.35;
    
    // 适中整体亮度
    finalColor = finalColor * 0.95;
    
    gl_FragColor = vec4(finalColor, alpha * 0.9);
  }
`

export function Foliage({ progress }: FoliageProps) {
  const pointsRef = useRef<THREE.Points>(null)
  const materialRef = useRef<THREE.ShaderMaterial>(null)
  
  // 生成粒子数据
  const { geometry, uniforms } = useMemo(() => {
    const count = TREE_CONFIG.particleCount
    
    const chaosPositions = new Float32Array(count * 3)
    const targetPositions = new Float32Array(count * 3)
    const sizes = new Float32Array(count)
    const colors = new Float32Array(count * 3)
    
    // 亮绿色 - 带金色调
    const greenColors = [
      new THREE.Color('#1a5c2e'), // 翠绿
      new THREE.Color('#228b22'), // 森林绿
      new THREE.Color('#2e8b57'), // 海绿
      new THREE.Color('#3cb371'), // 中海绿
    ]
    
    for (let i = 0; i < count; i++) {
      // 混沌位置
      const chaosPos = generateChaosPosition()
      chaosPositions[i * 3] = chaosPos.x
      chaosPositions[i * 3 + 1] = chaosPos.y
      chaosPositions[i * 3 + 2] = chaosPos.z
      
      // 目标位置（树形）
      const targetPos = generateFoliagePosition()
      targetPositions[i * 3] = targetPos.x
      targetPositions[i * 3 + 1] = targetPos.y
      targetPositions[i * 3 + 2] = targetPos.z
      
      // 随机大小
      sizes[i] = 2 + Math.random() * 4
      
      // 随机绿色
      const colorIndex = Math.floor(Math.random() * greenColors.length)
      const color = greenColors[colorIndex]
      colors[i * 3] = color.r
      colors[i * 3 + 1] = color.g
      colors[i * 3 + 2] = color.b
    }
    
    const geo = new THREE.BufferGeometry()
    // 使用targetPositions作为默认position，这样粒子有初始位置
    geo.setAttribute('position', new THREE.BufferAttribute(targetPositions.slice(), 3))
    geo.setAttribute('chaosPosition', new THREE.BufferAttribute(chaosPositions, 3))
    geo.setAttribute('targetPosition', new THREE.BufferAttribute(targetPositions, 3))
    geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1))
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    
    const unis = {
      progress: { value: 1.0 },
      time: { value: 0 },
    }
    
    return { geometry: geo, uniforms: unis }
  }, [])
  
  // 更新动画
  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uniforms.progress.value = progress
      materialRef.current.uniforms.time.value = state.clock.elapsedTime
    }
  })
  
  return (
    <points ref={pointsRef}>
      <primitive object={geometry} attach="geometry" />
      <shaderMaterial
        ref={materialRef}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  )
}